// src/services/kakaoService.js
// ─────────────────────────────────────────────────────────────
//  카카오 API 통합 서비스
//    - 카카오 지도 (지오코딩 / 역지오코딩 / 거리 계산 / 마커)
//    - 카카오 주소 검색 (우편번호 서비스)
//    - 카카오 로그인 (선택)
//
//  .env.local 필수 변수:
//    REACT_APP_KAKAO_MAP_KEY=발급받은_JavaScript_키
//
//  index.html <head> 에 추가:
//    <script src="//dapi.kakao.com/v2/maps/sdk.js
//      ?appkey=%REACT_APP_KAKAO_MAP_KEY%
//      &libraries=services,clusterer,drawing" defer></script>
// ─────────────────────────────────────────────────────────────

/* global kakao */

// ─────────────────────────────────────────────────────────────
//  § 1. SDK 로드 대기
// ─────────────────────────────────────────────────────────────

/**
 * 카카오 지도 SDK가 완전히 로드될 때까지 대기
 * @returns {Promise<void>}
 */
export function waitForKakao() {
  return new Promise((resolve, reject) => {
    if (window.kakao?.maps?.services) {
      resolve();
      return;
    }
    let attempts = 0;
    const id = setInterval(() => {
      attempts++;
      if (window.kakao?.maps?.services) {
        clearInterval(id);
        resolve();
      }
      if (attempts > 50) {
        clearInterval(id);
        reject(
          new Error(
            '카카오 지도 SDK 로드 실패. JavaScript 키와 도메인 등록을 확인하세요.'
          )
        );
      }
    }, 200);
  });
}

// ─────────────────────────────────────────────────────────────
//  § 2. 지도 초기화
// ─────────────────────────────────────────────────────────────

/**
 * 컨테이너 div에 카카오 지도 생성
 * @param {HTMLElement} container
 * @param {{ lat?: number, lng?: number, level?: number }} options
 * @returns {kakao.maps.Map}
 */
export function createMap(container, { lat = 37.5665, lng = 126.978, level = 5 } = {}) {
  const options = {
    center: new kakao.maps.LatLng(lat, lng),
    level,
  };
  const map = new kakao.maps.Map(container, options);

  // 지도 타입 컨트롤 (일반/스카이뷰)
  const mapTypeControl = new kakao.maps.MapTypeControl();
  map.addControl(mapTypeControl, kakao.maps.ControlPosition.TOPRIGHT);

  // 줌 컨트롤
  const zoomControl = new kakao.maps.ZoomControl();
  map.addControl(zoomControl, kakao.maps.ControlPosition.RIGHT);

  return map;
}

/**
 * 지도 중심 이동
 */
export function panTo(map, lat, lng, level) {
  map.setCenter(new kakao.maps.LatLng(lat, lng));
  if (level !== undefined) map.setLevel(level);
}

// ─────────────────────────────────────────────────────────────
//  § 3. 지오코딩 (주소 ↔ 좌표)
// ─────────────────────────────────────────────────────────────

/**
 * 주소 → 좌표 변환
 * @param {string} address 도로명 또는 지번 주소
 * @returns {Promise<{ lat: number, lng: number, roadAddress: string, jibunAddress: string }>}
 */
export function geocodeAddress(address) {
  return new Promise((resolve, reject) => {
    const geocoder = new kakao.maps.services.Geocoder();
    geocoder.addressSearch(address, (result, status) => {
      if (status === kakao.maps.services.Status.OK && result.length > 0) {
        const r = result[0];
        resolve({
          lat:          parseFloat(r.y),
          lng:          parseFloat(r.x),
          roadAddress:  r.road_address?.address_name ?? '',
          jibunAddress: r.address?.address_name      ?? '',
        });
      } else {
        reject(new Error(`주소를 찾을 수 없습니다: "${address}"`));
      }
    });
  });
}

/**
 * 좌표 → 주소 변환 (역지오코딩)
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{ road: string, jibun: string }>}
 */
export function reverseGeocode(lat, lng) {
  return new Promise((resolve, reject) => {
    const geocoder = new kakao.maps.services.Geocoder();
    const coord    = new kakao.maps.LatLng(lat, lng);

    geocoder.coord2Address(coord.getLng(), coord.getLat(), (result, status) => {
      if (status === kakao.maps.services.Status.OK) {
        resolve({
          road:  result[0]?.road_address?.address_name ?? '',
          jibun: result[0]?.address?.address_name      ?? '알 수 없는 주소',
        });
      } else {
        reject(new Error('역지오코딩 실패'));
      }
    });
  });
}

/**
 * 키워드로 장소 검색 (카카오 장소 검색 API)
 * @param {string} keyword
 * @param {{ lat?: number, lng?: number, radius?: number, size?: number }} options
 * @returns {Promise<Array<{ name, address, lat, lng, phone, category, placeUrl }>>}
 */
export function searchPlacesByKeyword(keyword, { lat, lng, radius = 5000, size = 15 } = {}) {
  return new Promise((resolve, reject) => {
    const ps = new kakao.maps.services.Places();
    const options = { size };

    if (lat && lng) {
      options.location = new kakao.maps.LatLng(lat, lng);
      options.radius   = radius;
    }

    ps.keywordSearch(keyword, (result, status, pagination) => {
      if (status === kakao.maps.services.Status.OK) {
        resolve({
          places: result.map((p) => ({
            id:          p.id,
            name:        p.place_name,
            address:     p.road_address_name || p.address_name,
            lat:         parseFloat(p.y),
            lng:         parseFloat(p.x),
            phone:       p.phone,
            category:    p.category_name,
            placeUrl:    p.place_url,
          })),
          hasMore:      pagination.hasNextPage,
          currentPage:  pagination.current,
          totalCount:   pagination.totalCount,
          paginate:     (page) => pagination.gotoPage(page),
        });
      } else if (status === kakao.maps.services.Status.ZERO_RESULT) {
        resolve({ places: [], hasMore: false, currentPage: 1, totalCount: 0 });
      } else {
        reject(new Error(`장소 검색 실패: ${status}`));
      }
    }, options);
  });
}

// ─────────────────────────────────────────────────────────────
//  § 4. 현재 위치
// ─────────────────────────────────────────────────────────────

/**
 * 브라우저 Geolocation API로 현재 위치 획득
 * @returns {Promise<{ lat: number, lng: number, accuracy: number }>}
 */
export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('이 브라우저는 위치 서비스를 지원하지 않습니다.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat:      pos.coords.latitude,
          lng:      pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      (err) => {
        const messages = {
          1: '위치 권한이 거부되었습니다. 브라우저 설정에서 허용해 주세요.',
          2: '위치 정보를 가져올 수 없습니다.',
          3: '위치 요청 시간이 초과되었습니다.',
        };
        reject(new Error(messages[err.code] ?? err.message));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });
}

// ─────────────────────────────────────────────────────────────
//  § 5. 마커 & 커스텀 오버레이
// ─────────────────────────────────────────────────────────────

// 카테고리별 마커 색상
const MARKER_COLORS = {
  서비스: '#FF6B35',
  행사:   '#7C4DFF',
  유통:   '#00BCD4',
  물류:   '#FF9800',
  요식업: '#E91E63',
  홍보:   '#4CAF50',
  기타:   '#9E9E9E',
};

/**
 * 공고 커스텀 오버레이 마커 생성
 * @param {kakao.maps.Map} map
 * @param {{ id, lat, lng, title, pay, urgent, category }} job
 * @param {function} onClick
 * @returns {{ overlay: kakao.maps.CustomOverlay, remove: function }}
 */
export function createJobMarker(map, job, onClick) {
  if (!job.lat || !job.lng) return null;

  const pos     = new kakao.maps.LatLng(job.lat, job.lng);
  const color   = job.urgent ? '#FF4444' : (MARKER_COLORS[job.category] ?? '#1a1a2e');
  const badge   = job.urgent ? '🔥' : '💼';
  const markerId = `kakao-marker-${job.id}`;

  const content = `
    <div id="${markerId}" style="
      background:${color};
      color:#fff;
      padding:5px 10px;
      border-radius:20px;
      font-size:11px;
      font-weight:700;
      white-space:nowrap;
      cursor:pointer;
      box-shadow:0 2px 8px rgba(0,0,0,0.25);
      border:2px solid rgba(255,255,255,0.3);
      font-family:'Apple SD Gothic Neo','Noto Sans KR',sans-serif;
      position:relative;
    ">
      ${badge} ${Number(job.pay).toLocaleString()}원
      <div style="
        position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);
        width:0;height:0;
        border-left:5px solid transparent;
        border-right:5px solid transparent;
        border-top:6px solid ${color};
      "></div>
    </div>`;

  const overlay = new kakao.maps.CustomOverlay({
    map,
    position: pos,
    content,
    yAnchor: 1.35,
    zIndex:  job.urgent ? 10 : 5,
  });

  // DOM 클릭 이벤트 (CustomOverlay는 kakao 이벤트 미지원)
  const handler = (e) => {
    if (e.target.closest(`#${markerId}`)) onClick(job);
  };
  document.addEventListener('click', handler);

  return {
    overlay,
    remove: () => {
      overlay.setMap(null);
      document.removeEventListener('click', handler);
    },
  };
}

/**
 * 공고 목록 전체 마커 렌더링
 * @param {kakao.maps.Map} map
 * @param {Array} jobs
 * @param {function} onJobClick
 * @returns {Array} markerInstances — remove() 를 가진 객체 배열
 */
export function renderJobMarkers(map, jobs, onJobClick) {
  return jobs
    .filter((j) => j.lat && j.lng)
    .map((job) => createJobMarker(map, job, onJobClick))
    .filter(Boolean);
}

/**
 * 마커 인스턴스 전체 제거
 */
export function clearMarkers(markerInstances = []) {
  markerInstances.forEach((m) => m?.remove?.());
}

/**
 * 현재 위치 마커 (파란 점)
 */
export function createCurrentLocationMarker(map, lat, lng) {
  const pos     = new kakao.maps.LatLng(lat, lng);
  const content = `
    <div style="
      width:16px;height:16px;border-radius:50%;
      background:#4285F4;border:3px solid #fff;
      box-shadow:0 0 0 4px rgba(66,133,244,0.3);
    "></div>`;
  return new kakao.maps.CustomOverlay({ map, position: pos, content });
}

/**
 * 마커 클러스터러 (많은 마커를 묶어서 표시)
 * @param {kakao.maps.Map} map
 * @param {Array<kakao.maps.Marker>} markers
 * @returns {kakao.maps.MarkerClusterer}
 */
export function createClusterer(map, markers = []) {
  return new kakao.maps.MarkerClusterer({
    map,
    markers,
    averageCenter:   true,
    minLevel:        6,
    disableClickZoom: false,
    styles: [{
      width:       '44px',
      height:      '44px',
      background:  '#1a1a2e',
      borderRadius:'50%',
      color:       '#FFD60A',
      textAlign:   'center',
      fontWeight:  '900',
      lineHeight:  '44px',
      fontSize:    '14px',
    }],
  });
}

// ─────────────────────────────────────────────────────────────
//  § 6. 인포윈도우
// ─────────────────────────────────────────────────────────────

let activeInfoWindow = null;

/**
 * 마커에 인포윈도우 표시
 * @param {kakao.maps.Map} map
 * @param {kakao.maps.Marker|kakao.maps.CustomOverlay} marker
 * @param {string} content HTML 문자열
 */
export function showInfoWindow(map, marker, content) {
  if (activeInfoWindow) activeInfoWindow.close();

  const infoWindow = new kakao.maps.InfoWindow({ content, removable: true });
  infoWindow.open(map, marker);
  activeInfoWindow = infoWindow;
  return infoWindow;
}

export function closeInfoWindow() {
  if (activeInfoWindow) {
    activeInfoWindow.close();
    activeInfoWindow = null;
  }
}

// ─────────────────────────────────────────────────────────────
//  § 7. 거리 계산 (Haversine 공식)
// ─────────────────────────────────────────────────────────────

/**
 * 두 좌표 사이의 거리 계산
 * @returns {{ meters: number, display: string }}  예: { meters: 850, display: "850m" }
 */
export function calcDistance(lat1, lng1, lat2, lng2) {
  const R   = 6371000; // 지구 반지름 (미터)
  const dL  = ((lat2 - lat1) * Math.PI) / 180;
  const dG  = ((lng2 - lng1) * Math.PI) / 180;
  const a   =
    Math.sin(dL / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dG / 2) ** 2;
  const meters = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  let display;
  if (meters < 1000)      display = `${Math.round(meters)}m`;
  else if (meters < 10000) display = `${(meters / 1000).toFixed(1)}km`;
  else                     display = `${Math.round(meters / 1000)}km`;

  return { meters, display };
}

/**
 * 내 위치 기준으로 공고 배열에 거리 정보 추가 + 정렬
 * @param {Array} jobs
 * @param {{ lat: number, lng: number }} myPos
 * @returns {Array} 거리순 정렬된 공고 배열
 */
export function sortJobsByDistance(jobs, myPos) {
  if (!myPos) return jobs;
  return jobs
    .map((job) => {
      if (!job.lat || !job.lng) return { ...job, distance: null, distanceMeters: Infinity };
      const { meters, display } = calcDistance(myPos.lat, myPos.lng, job.lat, job.lng);
      return { ...job, distance: display, distanceMeters: meters };
    })
    .sort((a, b) => a.distanceMeters - b.distanceMeters);
}

// ─────────────────────────────────────────────────────────────
//  § 8. 카카오 우편번호 검색 서비스
// ─────────────────────────────────────────────────────────────

/**
 * 카카오 우편번호 검색 팝업 열기
 * Daum 우편번호 서비스 SDK 필요 (자동 로드됨)
 *
 * index.html에 추가:
 *   <script src="//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js" defer></script>
 *
 * @param {function} onComplete - ({ zonecode, roadAddress, jibunAddress }) => void
 */
export function openAddressSearch(onComplete) {
  // SDK 로드 확인
  if (!window.daum?.Postcode) {
    // 동적 로드
    const script = document.createElement('script');
    script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.onload = () => _openPostcode(onComplete);
    document.head.appendChild(script);
    return;
  }
  _openPostcode(onComplete);
}

function _openPostcode(onComplete) {
  new window.daum.Postcode({
    oncomplete(data) {
      onComplete({
        zonecode:     data.zonecode,
        roadAddress:  data.roadAddress   || data.autoRoadAddress,
        jibunAddress: data.jibunAddress  || data.autoJibunAddress,
        buildingName: data.buildingName,
        apartment:    data.apartment === 'Y',
      });
    },
    // 팝업 스타일
    width:  '100%',
    height: '100%',
  }).open();
}

// ─────────────────────────────────────────────────────────────
//  § 9. 지도 경계 범위 계산 (현재 지도 영역 내 공고 필터링)
// ─────────────────────────────────────────────────────────────

/**
 * 현재 지도 화면에 보이는 공고만 반환
 * @param {kakao.maps.Map} map
 * @param {Array} jobs
 * @returns {Array}
 */
export function getJobsInBounds(map, jobs) {
  const bounds = map.getBounds();
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();

  return jobs.filter((job) => {
    if (!job.lat || !job.lng) return false;
    return (
      job.lat >= sw.getLat() && job.lat <= ne.getLat() &&
      job.lng >= sw.getLng() && job.lng <= ne.getLng()
    );
  });
}

/**
 * 공고 목록 전체가 보이도록 지도 범위 자동 조절
 * @param {kakao.maps.Map} map
 * @param {Array} jobs
 */
export function fitBoundsToJobs(map, jobs) {
  const validJobs = jobs.filter((j) => j.lat && j.lng);
  if (validJobs.length === 0) return;

  const bounds = new kakao.maps.LatLngBounds();
  validJobs.forEach((j) => bounds.extend(new kakao.maps.LatLng(j.lat, j.lng)));
  map.setBounds(bounds, 80); // 80px 패딩
}
